import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { useMemo } from 'react';
import dayjs from 'dayjs';

interface ComparisonChartProps {
  points: Array<{ timestamp: string; x: number; y: number }>;
  xLabel: string;
  yLabel: string;
  xUnit: string;
  yUnit: string;
  regression?: {
    line: { x: number; y: number }[];
    confidenceBand: { x: number; yMin: number; yMax: number }[];
  };
  tooltip?: (node: any) => React.ReactNode;
}

interface Point {
  x: number;
  y: number;
  timestamp?: Date;
}

export function ComparisonChart({
  points: apiPoints,
  xLabel,
  yLabel,
  xUnit,
  yUnit,
  regression,
  tooltip,
}: ComparisonChartProps) {
  const { scatterData, confidenceBand, regressionLine, xDomain, yDomain } = useMemo(() => {
    // Convert API points to scatter plot format
    const points = apiPoints.map((point) => ({
      x: point.x,
      y: point.y,
      timestamp: new Date(point.timestamp),
    }));

    // Use regression data from API if available, otherwise calculate it
    let line: Point[] = [];
    let band: Array<{ x: number; yMin: number; yMax: number }> = [];

    if (regression) {
      line = regression.line;
      band = regression.confidenceBand;
    }

    // Helper function to find if point is an outlier (outside confidence band)
    const isOutlier = (point: Point) => {
      // Find the closest band point by x value
      if (band.length === 0) return false;
      const closest = band.reduce((prev, curr) =>
        Math.abs(curr.x - point.x) < Math.abs(prev.x - point.x) ? curr : prev,
      );
      // Points outside the confidence band (above or below) are outliers
      return point.y > closest.yMax || point.y < closest.yMin;
    };

    // Separate normal points and outliers
    const outliers = points.filter(isOutlier);
    const normalPoints = points.filter((p) => !isOutlier(p));

    // Calculate x-axis domain (min and max temperature)
    const xValues = points.map((p) => p.x);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);

    // Calculate y-axis domain including confidence band with padding
    const yValues = points.map((p) => p.y);
    const bandYValues = band.flatMap((b) => [b.yMin, b.yMax]);
    const allYValues = [...yValues, ...bandYValues];
    const yMinRaw = Math.min(...allYValues);
    const yMaxRaw = Math.max(...allYValues);

    // Add 10% padding to y-axis
    const yRange = yMaxRaw - yMinRaw;
    const yPadding = yRange * 0.1;
    const yMin = yMinRaw - yPadding;
    const yMax = yMaxRaw + yPadding;

    return {
      scatterData: [
        {
          id: 'normal',
          data: normalPoints,
        },
        {
          id: 'outliers',
          data: outliers,
        },
      ],
      confidenceBand: band,
      regressionLine: line,
      xDomain: { min: xMin, max: xMax },
      yDomain: { min: yMin, max: yMax },
    };
  }, [apiPoints, regression]);

  return (
    <div className="w-full">
      <ResponsiveScatterPlot
        data={scatterData}
        margin={{ top: 40, right: 40, bottom: 60, left: 60 }}
        xScale={{
          type: 'linear',
          min: xDomain.min,
          max: xDomain.max,
          nice: false,
        }}
        yScale={{
          type: 'linear',
          min: yDomain.min,
          max: yDomain.max,
          nice: false,
        }}
        blendMode="normal"
        colors={['#00ffc3', '#ef4444']}
        nodeSize={10}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          legend: `${xLabel}${xUnit ? ` (${xUnit})` : ''}`,
          legendPosition: 'middle',
          legendOffset: 46,
        }}
        axisLeft={{
          legend: `${yLabel}${yUnit ? ` (${yUnit})` : ''}`,
          legendPosition: 'middle',
          legendOffset: -50,
        }}
        theme={{
          background: 'transparent',
          text: { fill: '#fff' },
          grid: { line: { stroke: '#444', strokeWidth: 1 } },
          tooltip: {
            container: {
              background: '#1f2937',
              color: '#fff',
            },
          },
        }}
        tooltip={tooltip}
        layers={[
          'grid',
          'axes',
          ({ xScale, yScale }) => {
            // Shaded area from regression line to x-axis
            if (regressionLine.length === 0) return null;
            const yMin = yScale.domain()[0];
            const bottomPoints = regressionLine.map((p) => [xScale(p.x), yScale(yMin)]);
            const topPoints = regressionLine.map((p) => [xScale(p.x), yScale(p.y)]).reverse();
            const shadePoints = [...bottomPoints, ...topPoints];

            return (
              <path
                key="regression-shade"
                d={`M ${shadePoints.map((p) => p.join(',')).join(' L ')} Z`}
                fill="rgba(0,255,200,0.05)"
                stroke="none"
              />
            );
          },
          ({ xScale, yScale }) => {
            if (confidenceBand.length === 0) return null;
            const areaPoints = confidenceBand.map((p) => [xScale(p.x), yScale(p.yMin)]);
            const areaPointsUpper = confidenceBand.map((p) => [xScale(p.x), yScale(p.yMax)]).reverse();
            const allPoints = [...areaPoints, ...areaPointsUpper];

            return (
              <path
                key="confidence-band"
                d={`M ${allPoints.map((p) => p.join(',')).join(' L ')} Z`}
                fill="rgba(0,255,200,0.15)"
                stroke="none"
              />
            );
          },
          ({ xScale, yScale }) => {
            if (regressionLine.length === 0) return null;
            const path = regressionLine.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.x)},${yScale(p.y)}`).join(' ');
            return <path key="regression-line" d={path} stroke="#00ffc3" strokeWidth={2} fill="none" />;
          },
          'nodes',
          'mesh',
        ]}
      />
    </div>
  );
}
